import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import Layout, { Empty } from '@/components/Layout'
import { Section } from '@/components/ui'
import AudioList from '@/components/AudioList'
import PhotoViewer from '@/components/PhotoViewer'
import type { Attachment } from '@/components/AttachmentPicker'
import { toast } from '@/components/Toast'
import type { SharedProps } from '@/types'
import { t } from '@/lib/i18n'

// 作业附件相册。**不是稿子里的东西** —— 用户在 S6 之后问「上传以后的照片和音频，
// 在哪里可以看到啊？」附件不进流水，原来只能按天 / 按条目翻。
//
// 刻意不做成 /homework 的第四段：那三段（今天 / 要交 / 等签名）是稿子定的结构，
// 而相册的时间语义与它们都不同（按时间回看 vs 按待办组织）。

/** 相册里的一个附件。比 `Attachment` 多两样：属于哪一条、走哪条删除路由。 */
type GalleryItem = Attachment & {
  /** 'shot' = 天级作业图，'proof' = 条目级证据。**决定删除打哪条路由。** */
  source: 'shot' | 'proof'
  /** 属于哪一条作业。天级作业图为 null。 */
  item_body: string | null
}

interface Props {
  days: { on: string; total: number; items: GalleryItem[] }[]
  /** 该成员**全部**附件数（含 30 天窗口之外的）。 */
  total: number
  window_days: number
  truncated: boolean
  can_write: boolean
}

export default function Gallery({ days, total, window_days, truncated, can_write }: Props) {
  const locale = usePage<SharedProps>().props.locale
  const [viewing, setViewing] = useState<GalleryItem | null>(null)

  // 两条删除路由。作用域各自只认自己那种 attachment —— 选错就是**静默不删**
  // （S6 踩过一次：那时 viewing 只存了 Photo，删 proof 时打到了作业图那条）。
  const remove = (item: GalleryItem) => {
    setViewing(null)
    router.delete(
      item.source === 'proof' ? `/homework_proofs/${item.id}` : `/homework/photos/${item.id}`,
      {
        preserveScroll: true,
        // 删的是照片还是录音，说两种话 —— 「照片已删除」用在一段录音上
        // 会让人以为删错了东西。
        onSuccess: () => toast(
          item.kind === 'audio' ? t('homework.toast_audio_deleted') : t('homework.toast_photo_deleted'),
        ),
      },
    )
  }

  // 日期标题交给 Intl：自己拼「9 月 6 日 星期日」会把语序锁死在中文上。
  const dayLabel = (iso: string) =>
    new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', weekday: 'short' })
      .format(new Date(`${iso}T00:00`))

  return (
    <Layout bleed topbar={{ back: '/homework', title: t('homework.gallery_title') }}>
      {days.length === 0 ? (
        <Empty emoji="🖼️" title={t('homework.gallery_empty_title')}>
          {t('homework.gallery_empty_sub')}
        </Empty>
      ) : (
        <>
          {days.map((day) => {
            const photos = day.items.filter((i) => i.kind === 'photo')
            const audios = day.items.filter((i) => i.kind === 'audio')
            return (
              <Section
                key={day.on}
                title={
                  // 段头整块可点，跳回那一天的作业 —— 相册的价值一半是
                  // 「这张是哪天哪条的」，看到了总会想回去看上下文。
                  <button
                    type="button"
                    onClick={() => router.get('/homework', { date: day.on })}
                    className="flex items-center gap-1.5 min-h-tap -my-2 text-left"
                  >
                    🗓 {dayLabel(day.on)}
                    <span aria-hidden="true" className="text-faint">›</span>
                  </button>
                }
                tail={t('homework.gallery_day_count', { count: day.total })}
              >
                {photos.length > 0 && (
                  // **网格**而不是横滑条：在作业页里图是「对照着录」的参照，
                  // 横滑是选择；在相册里图就是主内容，要一眼看全。
                  // 同看板热力图那条教训（横滑在那里是缺陷，S3 修掉了）。
                  <div className="grid grid-cols-3 gap-1.5">
                    {photos.map((photo) => (
                      <button
                        key={photo.id}
                        type="button"
                        onClick={() => setViewing(photo)}
                        aria-label={
                          photo.item_body
                            ? t('homework.gallery_photo_aria', { body: photo.item_body })
                            : t('homework.gallery_shot_aria')
                        }
                        className="aspect-square rounded-ctl overflow-hidden bg-sunk active:opacity-80"
                      >
                        {/* lazy：一年之后这一页可能几十上百张图 */}
                        <img
                          src={photo.url}
                          alt={photo.item_body ?? t('homework.photo_alt')}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* 录音走同一个列表组件（播放 / 进度 / 删除都已经在里面） */}
                <AudioList items={audios} onDelete={can_write ? remove : undefined} />
              </Section>
            )
          })}

          {/* 如实说：只列了最近 N 天，但总数是全部。静默截断会让人以为附件丢了。 */}
          <p className="mx-4 mt-6 mb-2 text-faint text-xs leading-[1.6]">
            {truncated
              ? t('homework.gallery_truncated', { days: window_days, count: total })
              : t('homework.gallery_total', { count: total })}
          </p>
        </>
      )}

      <PhotoViewer
        photo={viewing}
        onClose={() => setViewing(null)}
        onDelete={can_write ? remove : undefined}
      />
    </Layout>
  )
}
